// Shared cookie-consent state. Categories map to Google Consent Mode v2 signals.
// Essential is always on (auth, session, consent itself) and cannot be toggled.

export const CONSENT_STORAGE_KEY = "cookie-consent-v2";
const LEGACY_KEY = "cookie-consent-v1";

export type ConsentValue = "granted" | "denied";

export type ConsentCategories = {
  essential: true; // always on
  analytics: boolean;
  advertising: boolean;
};

export type StoredConsent = {
  analytics: ConsentValue;
  advertising: ConsentValue;
  updatedAt: string;
};

export const DEFAULT_DENIED: StoredConsent = {
  analytics: "denied",
  advertising: "denied",
  updatedAt: "",
};

export const ALL_GRANTED: Omit<StoredConsent, "updatedAt"> = {
  analytics: "granted",
  advertising: "granted",
};

export const ALL_DENIED: Omit<StoredConsent, "updatedAt"> = {
  analytics: "denied",
  advertising: "denied",
};

type ConsentListener = (c: StoredConsent | null) => void;
const listeners = new Set<ConsentListener>();

export function subscribeConsent(fn: ConsentListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(c: StoredConsent | null) {
  listeners.forEach((fn) => {
    try { fn(c); } catch { /* ignore */ }
  });
}

export function readConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<StoredConsent>;
      if (parsed && (parsed.analytics === "granted" || parsed.analytics === "denied")) {
        return {
          analytics: parsed.analytics,
          advertising: parsed.advertising === "granted" ? "granted" : "denied",
          updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : "",
        };
      }
    }
    // Migrate legacy v1 ("granted" | "denied" string) once.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === "granted" || legacy === "denied") {
      const migrated: StoredConsent = {
        analytics: legacy,
        advertising: legacy,
        updatedAt: new Date().toISOString(),
      };
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(migrated));
      localStorage.removeItem(LEGACY_KEY);
      return migrated;
    }
  } catch { /* ignore */ }
  return null;
}

export function pushConsentToGtag(c: Pick<StoredConsent, "analytics" | "advertising">) {
  if (typeof window === "undefined") return;
  const gtag = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag;
  if (typeof gtag !== "function") return;
  gtag("consent", "update", {
    ad_storage: c.advertising,
    ad_user_data: c.advertising,
    ad_personalization: c.advertising,
    analytics_storage: c.analytics,
  });
}

export function saveConsent(c: Pick<StoredConsent, "analytics" | "advertising">): StoredConsent {
  const next: StoredConsent = { ...c, updatedAt: new Date().toISOString() };
  try { localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  pushConsentToGtag(next);
  if (typeof window !== "undefined") {
    try { window.dispatchEvent(new CustomEvent("lovable:consent", { detail: next })); } catch { /* ignore */ }
  }
  emit(next);
  return next;
}

export function clearConsent() {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    localStorage.removeItem(LEGACY_KEY);
  } catch { /* ignore */ }
  // Reset gtag back to denied defaults until the user chooses again.
  pushConsentToGtag({ analytics: "denied", advertising: "denied" });
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent("lovable:consent", {
        detail: { analytics: "denied", advertising: "denied" },
      }));
    } catch { /* ignore */ }
  }
  emit(null);
}

